import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ActionButton } from '@adsk/alloy-react-menu';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import { useLocale } from '@adsk/alloy-react-locale';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useUser,
  useProjectEntitlements,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { LinkButton } from '@adsk/alloy-react-button';
import Avatar from '@adsk/alloy-react-avatar';
import { MemberAvatar } from '@adsk/acs-admin-ui-components-shared-components';
import {
  buildResendInvitationRequest,
  buildRemoveMemberRequest,
  getOptionsMenuItems,
} from './utils';
import {
  menuStyle,
  confirmationModalStyle,
  nameStyle,
  emailStyle,
  descriptionStyle,
  flexContainer,
  avatarContainer,
} from './styles';

const ProjectMemberOptionsMenu = ({
  email,
  horizontal,
  imageUrl,
  name,
  onResend,
  onRemove,
  status,
  style,
  userId,
  useWriteConstructionApis,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const userData = useUser();
  const currentProjectId = useCurrentProjectId();
  const { currentProject } = useProjectEntitlements();
  const { showNotification } = useNotifications();

  const user = userData?.user;
  const currentProjectName = currentProject?.name;

  const latestOnResend = useRef(onResend);
  const latestOnRemove = useRef(onRemove);

  const {
    data: resendInvitationData,
    error: resendInvitationError,
    doFetch: doFetchResendInvitation,
  } = useDataApi();
  const {
    data: removeMemberData,
    error: removeMemberError,
    loading: isRemovingMember,
    doFetch: doFetchRemoveMember,
  } = useDataApi();

  const handleRemoveClick = useCallback((event) => {
    event.stopPropagation();
  
    setShowConfirmation(true);
  }, []);

  const handleConfirmationRemoveClick = () => {
    doFetchRemoveMember(
      ...buildRemoveMemberRequest(
        currentProjectId,
        userId,
        useWriteConstructionApis
      )
    );
  };

  const handleResendClick = useCallback((event) => {
    event.stopPropagation();

    doFetchResendInvitation(
      ...buildResendInvitationRequest(currentProjectId, userId)
    );
  }, [currentProjectId, userId, doFetchResendInvitation]);

  const actionOptions = useMemo(() => getOptionsMenuItems(
    status,
    T.translate('REMOVE_MEMBER'),
    handleRemoveClick,
    T.translate('RESEND_INVITATION'),
    handleResendClick
  ), [T, status, handleRemoveClick, handleResendClick]);

  const ModalBody = () => {
    const projectName = currentProjectName || '';

    return (
      <>
        <div css={descriptionStyle}>
          {T.translate('REMOVE_MEMBER_MODAL_BODY', { projectName })}
        </div>
        <div css={flexContainer}>
          <div css={avatarContainer}>
            <MemberAvatar
              imageUrl={imageUrl}
              name={name}
              size={Avatar.SIZES.LARGE_48}
              status={status}
            />
          </div>
          <div>
            <div css={nameStyle}>{name}</div>
            <a href={`mailto:${email}`} css={emailStyle}>
              {email}
            </a>
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    latestOnResend.current = onResend;
  }, [onResend]);

  useEffect(() => {
    latestOnRemove.current = onRemove;
  }, [onRemove]);

  useEffect(() => {
    if (resendInvitationData === null) return;

    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('RESEND_INVITATION_SUCCESS', {
        email,
      }),
    });
    latestOnResend.current(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendInvitationData]);

  useEffect(() => {
    if (resendInvitationError === null) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('RESEND_INVITATION_ERROR', {
        email,
        TryAgainButton: (
          <LinkButton onClick={handleResendClick}>
            {T.translate('TRY_AGAIN')}
          </LinkButton>
        ),
      }),
    });
    latestOnResend.current(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendInvitationError]);

  useEffect(() => {
    if (!removeMemberData) return;

    setShowConfirmation(false);
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('REMOVE_MEMBER_SUCCESS', {
        name,
        email,
      }),
    });
    latestOnRemove.current(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberData]);

  useEffect(() => {
    if (!removeMemberError) return;

    setShowConfirmation(false);
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('REMOVE_MEMBER_ERROR', {
        name,
        email,
        TryAgainButton: (
          <LinkButton onClick={handleConfirmationRemoveClick}>
            {T.translate('TRY_AGAIN')}
          </LinkButton>
        ),
      }),
    });
    latestOnRemove.current(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberError]);

  return (
    <>
      {user && user.id !== userId && (
        <ActionButton
          horizontal={horizontal}
          options={actionOptions}
          onClick={(e) => {
            e.stopPropagation();
          }}
          menuStyle={menuStyle}
          data-testid="ProjectMemberProfileOptionsMenu_icon"
          style={style}
        />
      )}
      <ModalConfirmation
        show={showConfirmation}
        style={confirmationModalStyle}
        disableModalExit
        title={T.translate('REMOVE_MEMBER_MODAL_TITLE')}
        onClose={() => setShowConfirmation(false)}
        message={<ModalBody />}
        cancelButtonLabel={T.translate('CANCEL_BUTTON')}
        confirmationButtonLabel={T.translate('REMOVE_BUTTON')}
        isConfirmationButtonAlert
        isOnConfirmInProgress={isRemovingMember}
        onConfirm={handleConfirmationRemoveClick}
        data-testid="RemoveMember_ModalConfirmation"
      />
    </>
  );
};

ProjectMemberOptionsMenu.propTypes = {
  /** Project/Account member's email */
  email: PropTypes.string.isRequired,
  /** Menu button with horizontal or vertical dots */
  horizontal: PropTypes.bool,
  /** Project/Account member's profile image url */
  imageUrl: PropTypes.string,
  /** Project/Account member's name */
  name: PropTypes.string.isRequired,
  /** Callback executed when the user clicks on the Remove member option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onRemove: PropTypes.func.isRequired,
  /** Callback executed when the user clicks on the Resend invitation option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onResend: PropTypes.func.isRequired,
  /** Project/Account member's invitation status */
  status: PropTypes.string.isRequired,
  /** Styles applied to the menu button */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Project/Account member's user id */
  userId: PropTypes.string.isRequired,
  /** FF value of acc-replace-write-construction-apis */
  useWriteConstructionApis: PropTypes.bool,
};

ProjectMemberOptionsMenu.defaultProps = {
  horizontal: false,
  imageUrl: undefined,
  style: {},
  useWriteConstructionApis: false,
};

export default ProjectMemberOptionsMenu;
